import { AlertTriangle, Check, ChevronRight, Copy, Link2 } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';

import useAlert from 'src/hooks/useAlert';

import { PRIORITY_BADGE_COPY, TASK_DISPLAY_STATUS, TASK_PRIORITY } from '../../constants';
import {
  copyTextToClipboard,
  formatDue,
  getPatient,
  getTaskDeepLinkUrl,
  getTaskDisplayStatus,
  getTaskDisplayTitle,
  isAssessmentTask,
  isLinkedTask,
  isMessageTask,
  isTaskBlockedStatus,
  isTaskEscalatedStatus,
  isWorkflowBlockedTask,
} from '../../helpers';
import { Badge } from '../Badge';
import styles from './TaskRow.module.scss';

const TaskRow = ({
  task,
  compact,
  onSelect,
  selected,
  onAssignToMe,
  isAssigning,
  isLinkedChild,
  linkedParentTitle,
  linkedChildCount,
  linkedExpanded,
  onToggleLinked,
}) => {
  const { showAlert, AlertType } = useAlert();

  if (!task) return null;

  const patient = task?.patient ?? getPatient(task?.patientId);
  const due = task?.due ? formatDue(task.due) : { text: '—', urgent: false };
  const displayStatus = getTaskDisplayStatus(task);
  const isBlocked = isTaskBlockedStatus(task);
  const isEscalated = isTaskEscalatedStatus(task);
  const isCompleted = displayStatus === TASK_DISPLAY_STATUS.Done;
  const isLinked = isLinkedTask(task);
  const isBlockedByDependencies = isWorkflowBlockedTask(task);
  const taskPriority = task?.priority ?? TASK_PRIORITY.Low;
  const ownerLabel = `${task?.owner || ''}`.trim();
  const isUnassigned = !ownerLabel || ownerLabel.toLowerCase() === 'unassigned';
  const taskIsMessage = isMessageTask(task);
  const taskIsAssessment = isAssessmentTask(task);
  const taskTitle = getTaskDisplayTitle(task);
  const showPriorityBorder = !isCompleted;
  const priorityBorderClass = isLinked
    ? styles.rowPriorityLinked
    : styles[`rowPriority${taskPriority}`] || '';
  const showCriticalIcon =
    !isLinked &&
    !isBlocked &&
    showPriorityBorder &&
    taskPriority === TASK_PRIORITY.Critical &&
    !taskIsMessage &&
    !taskIsAssessment;
  const taskDeepLinkUrl = getTaskDeepLinkUrl(task);

  const handleCopyTaskLink = async (event) => {
    event.stopPropagation();
    if (!taskDeepLinkUrl) return;

    try {
      const didCopy = await copyTextToClipboard(taskDeepLinkUrl);
      if (!didCopy) throw new Error('Copy failed');
      showAlert(AlertType.Success, '', 'Task link copied to clipboard.');
    } catch (error) {
      showAlert(AlertType.Error, '', 'Unable to copy task link.');
    }
  };

  const showLinkedToggle = !compact && linkedChildCount > 0 && typeof onToggleLinked === 'function';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect?.(task)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect?.(task);
        }
      }}
      className={[
        styles.row,
        compact ? styles.compact : '',
        isCompleted ? styles.completedRow : '',
        isLinkedChild ? styles.linkedChildRow : '',
        selected ? styles.selected : '',
        !compact && showPriorityBorder ? priorityBorderClass : '',
      ].join(' ')}
    >
      {isCompleted ? (
        <div
          className={[
            styles.checkBox,
            taskPriority === TASK_PRIORITY.Critical ? styles.checkCritical : '',
            isBlocked ? styles.checkFlagged : '',
          ].join(' ')}
        >
          <Check className={styles.checkIcon} strokeWidth={2.4} />
        </div>
      ) : isLinked ? (
        <span className={styles.linkedIcon} aria-hidden="true">
          <Link2 className={styles.linkedIconGlyph} strokeWidth={1.8} />
        </span>
      ) : taskIsMessage ? (
        <span className={styles.messageIcon} aria-hidden="true" />
      ) : taskIsAssessment ? (
        <span className={styles.assessmentIcon} aria-hidden="true" />
      ) : showCriticalIcon ? (
        <span className={styles.criticalIcon} aria-hidden="true">
          <AlertTriangle className={styles.criticalIconGlyph} strokeWidth={2.2} />
        </span>
      ) : (
        <div
          className={[
            styles.checkBox,
            taskPriority === TASK_PRIORITY.Critical ? styles.checkCritical : '',
            isBlocked ? styles.checkFlagged : '',
          ].join(' ')}
        />
      )}
      <div className={styles.main}>
        <div className={styles.titleRow}>
          <span className={`${styles.title} ${isCompleted ? styles.completedTitle : ''}`}>
            {taskTitle}
          </span>
          {task.mustHave2026 && (
            <Badge variant="edge" className={styles.edgeBadge}>
              EDGE
            </Badge>
          )}
        </div>
        {task.caption && (
          <div className={`${styles.caption} ${compact ? styles.captionCompact : ''}`}>
            {task.caption}
          </div>
        )}
        {!compact && isLinked && linkedParentTitle ? (
          <div className={styles.waitingOnRow}>
            <Link2 className={styles.waitingOnIcon} strokeWidth={1.8} />
            <span className={styles.waitingOnLabel}>
              {isBlockedByDependencies ? 'Blocked by:' : 'Waiting on:'}
            </span>
            <span className={styles.waitingOnTitle}>{linkedParentTitle}</span>
          </div>
        ) : null}
        {!compact && (
          <div className={styles.metaRow}>
            {patient?.name} → {task.owner}
          </div>
        )}
      </div>
      {!compact && (
        <>
          <div className={styles.assignCell}>
            {isUnassigned ? (
              <button
                type="button"
                className={styles.assignButton}
                disabled={isAssigning}
                onClick={(event) => {
                  event.stopPropagation();
                  onAssignToMe?.(task.id);
                }}
              >
                {isAssigning ? 'Assigning...' : 'Assign to me'}
              </button>
            ) : null}
          </div>
          <div className={styles.statusCell}>
            {isBlocked ? (
              <Badge variant="critical">Blocked</Badge>
            ) : isEscalated ? (
              <Badge variant="escalated">Escalated</Badge>
            ) : (
              <Badge>{displayStatus}</Badge>
            )}
          </div>
          <div className={styles.priorityCell}>
            <span
              className={`${styles.priorityBadge} ${styles[`priorityBadge${taskPriority}`] || ''}`}
            >
              {PRIORITY_BADGE_COPY[taskPriority] ?? taskPriority}
            </span>
            <button
              type="button"
              className={styles.copyButton}
              onClick={handleCopyTaskLink}
              disabled={!taskDeepLinkUrl}
              aria-label="Copy task link"
              title="Copy task link"
            >
              <Copy className={styles.copyIcon} strokeWidth={1.8} />
            </button>
          </div>
        </>
      )}
      <span className={`${styles.dueText} ${due.urgent ? styles.dueUrgent : ''}`}>{due.text}</span>
      {showLinkedToggle ? (
        <button
          type="button"
          className={styles.linkedToggle}
          onClick={(event) => {
            event.stopPropagation();
            onToggleLinked?.(task.id);
          }}
        >
          <ChevronRight
            className={`${styles.linkedToggleChevron} ${linkedExpanded ? styles.linkedToggleChevronOpen : ''}`}
            strokeWidth={1.8}
          />
          <Link2 className={styles.linkedToggleIcon} strokeWidth={1.8} />
          <span>
            {linkedChildCount} linked {linkedChildCount === 1 ? 'task' : 'tasks'}
          </span>
        </button>
      ) : null}
    </div>
  );
};

TaskRow.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    patient: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
    patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    due: PropTypes.string,
    priority: PropTypes.string,
    status: PropTypes.number,
    team: PropTypes.string,
    owner: PropTypes.string,
    typeId: PropTypes.number,
    reasonId: PropTypes.number,
    title: PropTypes.string,
    assessmentType: PropTypes.string,
    assessmentVariant: PropTypes.string,
    mustHave2026: PropTypes.bool,
    caption: PropTypes.string,
    isVisible: PropTypes.bool,
    dependency: PropTypes.object,
  }),
  compact: PropTypes.bool,
  onSelect: PropTypes.func,
  selected: PropTypes.bool,
  onAssignToMe: PropTypes.func,
  isAssigning: PropTypes.bool,
  isLinkedChild: PropTypes.bool,
  linkedParentTitle: PropTypes.string,
  linkedChildCount: PropTypes.number,
  linkedExpanded: PropTypes.bool,
  onToggleLinked: PropTypes.func,
};

export default TaskRow;
